import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useChatAuth } from "@/context/ChatAuthContext";
import chatApi, { chatWsUrl } from "@/lib/chatApi";
import { toast } from "sonner";
import {
  Send, Loader2, LogOut, Hash, Users as UsersIcon, MessageCircle, Trash2, Shield, Ban, Crown, Zap,
} from "lucide-react";

function RoleBadge({ role }) {
  if (!role || role === "user") return null;
  const map = {
    admin: { label: "ADMIN", cls: "bg-yellow-400 text-black", icon: Crown },
    moderator: { label: "MOD", cls: "bg-sky-400 text-black", icon: Shield },
    team: { label: "TEAM", cls: "bg-emerald-500 text-black", icon: Zap },
  };
  const cfg = map[role];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

const ROLES = ["user", "team", "moderator", "admin"];
const PRIVILEGED = ["moderator", "admin"];

export default function Chat() {
  const { user, loading, logout } = useChatAuth();
  const navigate = useNavigate();

  const [channel, setChannel] = useState("public"); // "public" or "dm:<other_id>"
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [online, setOnline] = useState(new Set());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  // ---- Sockets ----
  useEffect(() => {
    if (!user) return;
    let ws;
    let stopped = false;

    function connect() {
      ws = new WebSocket(chatWsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        // immediately send a heartbeat to keep alive
      };
      ws.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          handleSocketEvent(payload);
        } catch {/* ignore */}
      };
      ws.onclose = () => {
        if (stopped) return;
        setTimeout(connect, 1500); // auto-reconnect
      };
      ws.onerror = () => ws.close();
    }
    connect();
    return () => { stopped = true; if (ws) ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const handleSocketEvent = useCallback((p) => {
    if (p.type === "presence") {
      setOnline(new Set(p.online || []));
      return;
    }
    if (p.type === "user_updated") {
      // re-fetch user list lazily
      fetchUsers();
      return;
    }
    if (p.type === "message") {
      setMessages((prev) => {
        // accept only if it matches current channel context
        const m = p.message;
        const inCurrent = m.channel === "public"
          ? channel === "public"
          : matchesDm(m.channel, channel, user?.user_id);
        if (!inCurrent) {
          // For DMs not currently open, show a toast
          if (m.sender_id !== user?.user_id) {
            toast(`${m.sender_name}: ${m.content.slice(0, 60)}`, { description: m.channel.startsWith("dm:") ? "New DM" : undefined });
          }
          return prev;
        }
        if (prev.find((x) => x.id === m.id)) return prev;
        return [...prev, m];
      });
      return;
    }
    if (p.type === "message_deleted") {
      setMessages((prev) => prev.map((m) => (m.id === p.id ? { ...m, deleted: true, content: "[removed by moderator]" } : m)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, user?.user_id]);

  // ---- Loaders ----
  const fetchMessages = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    try {
      const { data } = await chatApi.get("/messages", { params: { channel, limit: 100 } });
      setMessages(data);
    } catch (e) {
      toast.error("Could not load messages");
    } finally {
      setBusy(false);
    }
  }, [channel, user]);

  const fetchUsers = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await chatApi.get("/users");
      setUsers(data);
    } catch {/* ignore */}
  }, [user]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ---- Autoscroll ----
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, channel]);

  // ---- Send ----
  const send = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await chatApi.post("/messages", { channel, content });
      setInput("");
    } catch (err) {
      toast.error("Could not send");
    } finally {
      setSending(false);
    }
  };

  // ---- Mod actions ----
  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await chatApi.delete(`/messages/${id}`);
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const setRole = async (uid, role) => {
    try {
      await chatApi.post("/admin/role", { user_id: uid, role });
      toast.success(`Role set to ${role}`);
      fetchUsers();
    } catch {
      toast.error("Could not update role");
    }
  };

  const setBan = async (uid, banned) => {
    if (banned && !window.confirm("Ban this user?")) return;
    try {
      await chatApi.post("/admin/ban", { user_id: uid, banned });
      toast.success(banned ? "User banned" : "User unbanned");
      fetchUsers();
    } catch {
      toast.error("Could not update ban");
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/chat-login");
  };

  const otherInDm = useMemo(() => {
    if (!channel.startsWith("dm:")) return null;
    const otherId = channel.split(":", 2)[1];
    return users.find((u) => u.user_id === otherId);
  }, [channel, users]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-yellow-400 animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/chat-login" replace />;

  const iAmMod = PRIVILEGED.includes(user.role);
  const iAmAdmin = user.role === "admin";

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="chat-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0d0f12]">
        <div className="container-vg flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 border border-yellow-400/40 bg-yellow-400/5">
              <MessageCircle className="w-5 h-5 text-yellow-400" />
            </span>
            <div className="font-heading">
              <div className="text-white font-black text-base uppercase tracking-tight leading-none">Storm Comms</div>
              <div className="text-[10px] tracking-[0.28em] text-zinc-500 font-mono uppercase mt-1">Vortex Ghost</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-zinc-400 font-mono">{user.name}</span>
              <RoleBadge role={user.role} />
            </div>
            <button onClick={onLogout} className="btn-secondary !py-2 !px-3 !text-xs" data-testid="chat-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-0 overflow-hidden">
        {/* Sidebar */}
        <aside className="md:col-span-3 border-r border-white/5 overflow-y-auto bg-[#0d0f12]" data-testid="chat-sidebar">
          <div className="p-4 border-b border-white/5">
            <div className="overline mb-3">Channels</div>
            <button
              onClick={() => setChannel("public")}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
                channel === "public" ? "bg-yellow-400/10 text-yellow-400" : "text-zinc-300 hover:bg-white/[0.03]"
              }`}
              data-testid="chat-channel-public"
            >
              <Hash className="w-4 h-4" /> public
            </button>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Direct Messages</div>
              <span className="text-xs text-zinc-500 font-mono">{online.size} online</span>
            </div>
            <ul className="space-y-1" data-testid="chat-user-list">
              {users
                .filter((u) => u.user_id !== user.user_id)
                .map((u) => {
                  const dmCh = `dm:${u.user_id}`;
                  const active = channel === dmCh;
                  return (
                    <li key={u.user_id}>
                      <button
                        onClick={() => setChannel(dmCh)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
                          active ? "bg-yellow-400/10 text-yellow-400" : "text-zinc-300 hover:bg-white/[0.03]"
                        } ${u.banned ? "opacity-50 line-through" : ""}`}
                        data-testid={`chat-dm-${u.user_id}`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full ${online.has(u.user_id) ? "bg-green-500" : "bg-zinc-600"}`} />
                        <span className="truncate flex-1">{u.name}</span>
                        <RoleBadge role={u.role} />
                      </button>
                    </li>
                  );
                })}
            </ul>
          </div>

          {iAmAdmin && (
            <div className="p-4 border-t border-white/5">
              <div className="overline mb-3">Admin · Manage Roles</div>
              <ul className="space-y-2 text-xs">
                {users.map((u) => (
                  <li key={u.user_id} className="flex items-center gap-2" data-testid={`admin-user-${u.user_id}`}>
                    <span className="flex-1 truncate text-zinc-300">{u.name}</span>
                    <select
                      value={u.role}
                      onChange={(e) => setRole(u.user_id, e.target.value)}
                      className="bg-[#1A1D24] border border-white/10 text-zinc-200 px-1 py-1 font-mono text-[10px] uppercase"
                      data-testid={`admin-role-${u.user_id}`}
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button
                      onClick={() => setBan(u.user_id, !u.banned)}
                      className={`p-1 ${u.banned ? "text-yellow-400" : "text-red-400 hover:text-red-300"}`}
                      title={u.banned ? "Unban" : "Ban"}
                      data-testid={`admin-ban-${u.user_id}`}
                    >
                      <Ban className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Main chat */}
        <main className="md:col-span-9 flex flex-col h-[calc(100vh-4rem)]" data-testid="chat-main">
          {/* Channel header */}
          <div className="border-b border-white/5 px-6 py-4 bg-[#0d0f12] flex items-center gap-3">
            {channel === "public" ? (
              <>
                <Hash className="w-5 h-5 text-yellow-400" />
                <div>
                  <div className="font-heading text-lg font-semibold">public</div>
                  <div className="text-xs text-zinc-500 font-mono">Everyone — be kind, talk weather.</div>
                </div>
              </>
            ) : (
              <>
                <UsersIcon className="w-5 h-5 text-yellow-400" />
                <div className="flex-1">
                  <div className="font-heading text-lg font-semibold flex items-center gap-2">
                    {otherInDm?.name || "DM"}
                    <RoleBadge role={otherInDm?.role} />
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">Private conversation</div>
                </div>
              </>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-[#0A0A0A]" data-testid="chat-messages">
            {busy && messages.length === 0 ? (
              <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : messages.length === 0 ? (
              <div className="text-zinc-500 text-sm font-mono uppercase tracking-wider">No messages yet — say hello</div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="group flex items-start gap-3" data-testid={`chat-msg-${m.id}`}>
                  <div className="w-8 h-8 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 flex items-center justify-center font-mono text-xs uppercase shrink-0">
                    {(m.sender_name || "?").slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-semibold text-white">{m.sender_name}</span>
                      <RoleBadge role={m.sender_role} />
                      <span className="text-[10px] text-zinc-500 font-mono">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      {iAmMod && !m.deleted && (
                        <button
                          onClick={() => deleteMessage(m.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                          data-testid={`chat-delete-${m.id}`}
                          title="Delete message"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className={`text-zinc-200 mt-1 leading-relaxed text-sm whitespace-pre-wrap break-words ${m.deleted ? "italic text-zinc-500" : ""}`}>
                      {m.content}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-white/5 p-4 bg-[#0d0f12] flex items-center gap-3" data-testid="chat-input-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={channel === "public" ? "Message #public…" : `Message ${otherInDm?.name || ""}…`}
              className="flex-1 bg-[#1A1D24] border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-yellow-400"
              maxLength={2000}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="chat-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </main>
      </div>
    </div>
  );
}

function matchesDm(serverChannel, viewChannel, myId) {
  // server stores dm:<a>:<b> sorted. view is dm:<otherId>
  if (!viewChannel.startsWith("dm:")) return false;
  if (!serverChannel.startsWith("dm:")) return false;
  const otherId = viewChannel.split(":", 2)[1];
  const expected = `dm:${[myId, otherId].sort().join(":")}`;
  return serverChannel === expected;
}
